// src/pages/Numero/NumeroPoliceConfig.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { Button, Card, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { API_URL, TOKEN } from "../../../variables/constants";

const { Title, Text } = Typography;

/* ===================== Tokens pris en charge par TON backend ===================== *
 * Voir NumeroService.buildContratTokens(...) :
 * {COMPAGNIE} {POINT_VENTE} {CATEGORIE} {ORGANISME} {ANNEE2} {ANNEE4} + {PFX} + {SEQ:n}
 * IMPORTANT : pas de {CODE_COMPAGNIE} côté back → utilise {COMPAGNIE}.
 * ================================================================================= */

const HEADERS = {
  Authorization: `Bearer ${TOKEN}`,
  "Content-Type": "application/json",
};

/* ------------------------------ Exemples de patterns ----------------------------- */
const PRESET_PATTERNS = [
  { label: "PFX-ANNEE2-SEQ(5)", value: "{PFX}-{ANNEE2}-{SEQ:5}" },               // POL-25-00001
  { label: "COMPAGNIE-CATEGORIE-ANNEE2-SEQ(6)", value: "{COMPAGNIE}-{CATEGORIE}-{ANNEE2}-{SEQ:6}" },
  { label: "PFXANNEE4/SEQ(4)", value: "{PFX}{ANNEE4}/{SEQ:4}" },                  // POL2025/0001
  { label: "ORGANISME-POINT_VENTE-ANNEE2-SEQ(5)", value: "{ORGANISME}-{POINT_VENTE}-{ANNEE2}-{SEQ:5}" },
  { label: "PFX-CATEGORIEANNEE2-SEQ(6)", value: "{PFX}-{CATEGORIE}{ANNEE2}-{SEQ:6}" },
  { label: "COMPAGNIE-ANNEE4-SEQ(7)", value: "{COMPAGNIE}-{ANNEE4}-{SEQ:7}" },
];

/* ------------------------------ Helpers: preview -------------------------------- */
function previewPolice({ pattern, prefix, seq = 123, now = new Date(), tokens = {} }) {
  const y4 = String(now.getFullYear());
  const y2 = y4.slice(-2);
  const defaults = {
    COMPAGNIE: "12200",
    POINT_VENTE: "AG01",
    CATEGORIE: "M",
    ORGANISME: "1200",
    ANNEE2: y2,
    ANNEE4: y4,
    PFX: prefix || "",
  };
  const all = { ...defaults, ...tokens };

  // longueur SEQ
  const m = (pattern || "").match(/\{SEQ:(\d+)\}/);
  const len = m ? parseInt(m[1], 10) : 0;
  const seqStr = len ? String(seq).padStart(len, "0") : String(seq);

  let out = (pattern || "").replace(/\{SEQ:(\d+)\}/g, seqStr);
  Object.entries(all).forEach(([k, v]) => {
    out = out.replaceAll(`{${k}}`, v ?? "");
  });
  return out;
}

/* ------------------------------ Helpers: API calls ------------------------------- */
async function getConfig() {
  const res = await axios.get(`${API_URL}/api/numero-config`, { headers: HEADERS });
  return res.data || {};
}

async function putConfig(dto) {
  const res = await axios.put(`${API_URL}/api/numero-config`, dto, { headers: HEADERS });
  return res.data;
}

/* ============================== Modale d’édition ================================ */
function EditPoliceModal({ show, onClose, initialDto, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [numeroPolicePattern, setNumeroPolicePattern] = useState("");
  const [numeroPolicePrefix, setNumeroPolicePrefix] = useState("");
  const [seqExample, setSeqExample] = useState(123);

  useEffect(() => {
    if (show) {
      setNumeroPolicePattern(
        initialDto?.numeroPolicePattern || "{COMPAGNIE}-{CATEGORIE}-{ANNEE2}-{SEQ:6}"
      );
      setNumeroPolicePrefix(initialDto?.numeroPolicePrefix || "");
      setSeqExample(123);
    }
  }, [show, initialDto]);

  const preview = useMemo(
    () =>
      previewPolice({
        pattern: numeroPolicePattern,
        prefix: numeroPolicePrefix,
        seq: seqExample,
      }),
    [numeroPolicePattern, numeroPolicePrefix, seqExample]
  );

  const applyPreset = (val) => setNumeroPolicePattern(val);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!numeroPolicePattern?.trim()) {
      toast.warn("Le pattern ne peut pas être vide.");
      return;
    }

    // Merge avec l’attestation existante pour ne pas l’écraser
    const dto = {
      numeroPolicePattern,
      numeroPolicePrefix,
      numeroAttestationPattern: initialDto?.numeroAttestationPattern ?? null,
      numeroAttestationPrefix: initialDto?.numeroAttestationPrefix ?? null,
    };

    setLoading(true);
    try {
      const saved = await putConfig(dto);
      toast.success("Configuration Police mise à jour.");
      onSaved && onSaved(saved);
      onClose && onClose();
    } catch (err) {
      console.error(err);
      const s = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (s === 403
          ? "403: Accès refusé. Vérifiez votre TOKEN/rôle."
          : s === 415
          ? "415: Content-Type invalide. Envoyez bien du JSON."
          : "Erreur lors de la mise à jour.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Body>
        <button type="button" className="btn-close" onClick={onClose} />

        <div className="text-center mb-3">
          <h4>Modifier la configuration du numéro de police</h4>
          <p className="mb-0">
            Utilisez un pattern avec les tokens :{" "}
            <code>{`{COMPAGNIE} {POINT_VENTE} {CATEGORIE} {ORGANISME} {ANNEE2} {ANNEE4} {PFX} {SEQ:n}`}</code>
          </p>
        </div>

        {/* Presets */}
        <div className="mb-3">
          <label className="form-label">Modèles prêts à l’emploi</label>
          <div className="d-flex flex-wrap gap-2">
            {PRESET_PATTERNS.map((p) => (
              <button
                key={p.value}
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => applyPreset(p.value)}
                title={p.value}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-12">
            <label className="form-label">Pattern Police</label>
            <input
              type="text"
              className="form-control"
              value={numeroPolicePattern}
              onChange={(e) => setNumeroPolicePattern(e.target.value)}
              required
              placeholder="{PFX}-{ANNEE2}-{SEQ:5}"
            />
            <small className="text-muted">
              Règles : utilisez au plus un seul <code>{`{SEQ:n}`}</code> ; <code>n</code> est la
              longueur du padding (ex: 5 → 00001).
            </small>
          </div>

          <div className="col-6">
            <label className="form-label">Préfixe Police (PFX)</label>
            <input
              type="text"
              className="form-control"
              value={numeroPolicePrefix}
              onChange={(e) => setNumeroPolicePrefix(e.target.value)}
              placeholder="POL"
            />
          </div>

          <div className="col-3">
            <label className="form-label">Exemple séquence</label>
            <input
              type="number"
              className="form-control"
              value={seqExample}
              onChange={(e) => setSeqExample(Number(e.target.value))}
              min={0}
            />
          </div>

          <div className="col-12">
            <label className="form-label d-block">Aperçu</label>
            <div className="p-2 rounded bg-dark text-white" style={{ minHeight: 38 }}>
              {preview || "—"}
            </div>
            <small className="text-muted">
              La séquence réelle est gérée côté serveur et dépend de la{" "}
              <i>portée</i> (pattern sans <code>{`{SEQ}`}</code> avec tokens remplacés).
              Un changement de portée peut redémarrer le compteur.
            </small>
          </div>

          <div className="col-12 text-center mt-2">
            <button type="submit" className="btn btn-primary me-2" disabled={loading}>
              {loading ? "Enregistrement..." : "Enregistrer (PUT)"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Annuler
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}

/* ============================== Page d’enveloppe ================================ */
export default function NumeroPoliceConfig() {
  const [dto, setDto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await getConfig();
      setDto(data);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.status === 403
          ? "403: Accès refusé. Vérifiez votre TOKEN/rôle."
          : "Erreur lors du chargement de la configuration."
      );
      setDto(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Title level={4} style={{ marginBottom: 16 }}>
                CONFIGURATION DU NUMÉRO — POLICE
              </Title>

              <div className="mb-3">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => setShowEdit(true)}
                >
                  Modifier la configuration Police
                </Button>
              </div>

              <Card className="page-card" loading={loading}>
                {dto ? (
                  <div>
                    <p className="mb-2">
                      <Text type="secondary">Pattern actuel :</Text>
                      <br />
                      <code>{dto.numeroPolicePattern || "—"}</code>
                    </p>
                    <p className="mb-0">
                      <Text type="secondary">Préfixe actuel :</Text>
                      <br />
                      <code>{dto.numeroPolicePrefix || "—"}</code>
                    </p>
                  </div>
                ) : (
                  !loading && <Text type="secondary">Aucune configuration chargée.</Text>
                )}
              </Card>

              <EditPoliceModal
                show={showEdit}
                onClose={() => setShowEdit(false)}
                initialDto={dto}
                onSaved={(saved) => setDto(saved)}
              />
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
}
